import { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  getActiveAnnouncements,
  createOrUpdateAnnouncement,
  deactivateAnnouncement
} from '../services/announcements.service';
import { IBannerColor } from '../components/BannerColorSelector/BannerColorSelector';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';

const MAX_TEXT_LENGTH = 100;
const MAX_SUBJECT_LENGTH = 50;
const SPANISH_LANGUAGE = 'es';
const ENGLISH_LANGUAGE = 'en';

interface IOriginalData {
  subject: string;
  bannerText: string;
  bannerTextEn: string;
  selectedColor: string;
  isActive: boolean;
}

interface UseAnnouncementsProps {
  colors: IBannerColor[];
}

/**
 * Hook personalizado para manejar la lógica de anuncios
 * @param colors - Array de colores disponibles para los banners
 * @returns Objeto con estados, funciones y valores calculados para el componente de anuncios
 */
export const useAnnouncements = ({ colors }: UseAnnouncementsProps) => {
  const history = useHistory();
  const [subject, setSubject] = useState<string>('');
  const [bannerText, setBannerText] = useState<string>('');
  const [bannerTextEn, setBannerTextEn] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState<boolean>(false);
  const [showSaveBannerModal, setShowSaveBannerModal] = useState<boolean>(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [originalData, setOriginalData] = useState<IOriginalData | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const pendingNavigation = useRef<(() => void) | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);

  const hasSubjectError = (): boolean => {
    return subject.length > MAX_SUBJECT_LENGTH;
  };

  const hasBannerTextError = (): boolean => {
    return bannerText.length > MAX_TEXT_LENGTH;
  };

  const hasBannerTextEnError = (): boolean => {
    return bannerTextEn.length > MAX_TEXT_LENGTH;
  };

  const saveOriginalData = (data: IOriginalData) => {
    setOriginalData(data);
  };

  const loadAnnouncementData = async () => {
    try {
      setIsLoading(true);
      const announcements = await getActiveAnnouncements();

      if (announcements?.length > 0) {
        const activeAnnouncement = announcements?.[0];
        const loadedSubject = activeAnnouncement.subject || '';
        const loadedColor = activeAnnouncement.color || '';
        const loadedIsActive = activeAnnouncement.activeUntil === null;

        setSubject(loadedSubject);
        setSelectedColor(loadedColor);
        setIsActive(loadedIsActive);
        setIsEditMode(loadedIsActive);

        let loadedBannerText = '';
        let loadedBannerTextEn = '';

        if (activeAnnouncement?.contents && activeAnnouncement?.contents.length > 0) {
          const spanishContent = activeAnnouncement?.contents.find(
            (c: { language: string }) => c.language === SPANISH_LANGUAGE
          );
          const englishContent = activeAnnouncement?.contents.find(
            (c: { language: string }) => c.language === ENGLISH_LANGUAGE
          );

          loadedBannerText = spanishContent?.text || '';
          loadedBannerTextEn = englishContent?.text || '';
        }

        setBannerText(loadedBannerText);
        setBannerTextEn(loadedBannerTextEn);

        saveOriginalData({
          subject: loadedSubject,
          bannerText: loadedBannerText,
          bannerTextEn: loadedBannerTextEn,
          selectedColor: loadedColor,
          isActive: loadedIsActive
        });
      } else {
        const emptyData = {
          subject: '',
          bannerText: '',
          bannerTextEn: '',
          selectedColor: '',
          isActive: false
        };
        setSubject(emptyData.subject);
        setBannerText(emptyData.bannerText);
        setBannerTextEn(emptyData.bannerTextEn);
        setSelectedColor(emptyData.selectedColor);
        setIsActive(emptyData.isActive);
        saveOriginalData(emptyData);
      }
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Verifica si hay cambios sin guardar comparando con los datos originales
   */
  const checkUnsavedChanges = useCallback((): boolean => {
    if (!originalData) return false;

    const currentSubject = subject.trim();
    const currentBannerText = bannerText.trim();
    const currentBannerTextEn = bannerTextEn.trim();
    const currentSelectedColor = selectedColor;

    return (
      currentSubject !== originalData.subject ||
      currentBannerText !== originalData.bannerText ||
      currentBannerTextEn !== originalData.bannerTextEn ||
      currentSelectedColor !== originalData.selectedColor ||
      isActive !== originalData.isActive
    );
  }, [subject, bannerText, bannerTextEn, selectedColor, isActive, originalData]);

  /**
   * Confirma la salida descartando los cambios
   */
  const handleConfirmExit = () => {
    setShowUnsavedChangesModal(false);
    if (pendingNavigation.current) {
      pendingNavigation.current();
      pendingNavigation.current = null;
    }
  };

  /**
   * Cancela la salida y mantiene los cambios
   */
  const handleCancelExit = () => {
    setShowUnsavedChangesModal(false);
    pendingNavigation.current = null;
  };

  /**
   * Maneja el cambio del toggle para habilitar/deshabilitar el modo de edición
   */
  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement> | boolean) => {
    const newStatus = typeof e === 'boolean' ? e : e.target.checked;

    if (!newStatus && isActive) {
      setShowDeactivateModal(true);
      return;
    }

    setIsEditMode(newStatus);
  };

  /**
   * Confirma la desactivación del anuncio
   */
  const handleConfirmDeactivate = async () => {
    try {
      setIsLoading(true);
      await deactivateAnnouncement();
      setIsActive(false);
      setIsEditMode(false);
      setShowDeactivateModal(false);
      displaySuccessNotification({
        customJSXMessage: <>Banner desactivado correctamente</>
      });
      await loadAnnouncementData();
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDeactivate = () => {
    setShowDeactivateModal(false);
  };

  /**
   * Maneja el cambio del texto del banner en español
   */
  const handleBannerTextChange = (value: React.SetStateAction<string>) => {
    const newValue = typeof value === 'function' ? value(bannerText) : value;
    setBannerText(newValue);
  };

  /**
   * Maneja el cambio del texto del banner en inglés
   */
  const handleBannerTextEnChange = (value: React.SetStateAction<string>) => {
    const newValue = typeof value === 'function' ? value(bannerTextEn) : value;
    setBannerTextEn(newValue);
  };

  const handleSaveBanner = () => {
    if (!subject.trim()) {
      displayErrorNotification({ customJSXMessage: <>El asunto del anuncio es requerido</> });
      return;
    }

    if (subject.length > MAX_SUBJECT_LENGTH) {
      displayErrorNotification({ customJSXMessage: <>El asunto del anuncio no puede exceder {MAX_SUBJECT_LENGTH} caracteres</> });
      return;
    }

    if (!bannerText.trim()) {
      displayErrorNotification({ customJSXMessage: <>El texto del banner en español es requerido</> });
      return;
    }

    if (bannerText.length > MAX_TEXT_LENGTH) {
      displayErrorNotification({ customJSXMessage: <>El texto del banner en español no puede exceder {MAX_TEXT_LENGTH} caracteres</> });
      return;
    }

    if (bannerTextEn.length > MAX_TEXT_LENGTH) {
      displayErrorNotification({ customJSXMessage: <>El texto del banner en inglés no puede exceder {MAX_TEXT_LENGTH} caracteres</> });
      return;
    }

    if (!selectedColor) {
      displayErrorNotification({ customJSXMessage: <>El color del banner es requerido</> });
      return;
    }

    setShowSaveBannerModal(true);
  };

  const handleConfirmSaveBanner = async () => {
    try {
      setIsSaving(true);
      const contents = [
        {
          text: bannerText.trim(),
          language: SPANISH_LANGUAGE
        }
      ];

      if (bannerTextEn.trim()) {
        contents.push({
          text: bannerTextEn.trim(),
          language: ENGLISH_LANGUAGE
        });
      }

      const announcementData = {
        subject: subject.trim(),
        color: selectedColor,
        contents
      };

      await createOrUpdateAnnouncement(announcementData);
      setIsActive(true);
      setShowSaveBannerModal(false);
      displaySuccessNotification({
        customJSXMessage: <>Banner publicado correctamente</>
      });
      await loadAnnouncementData();
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelSaveBanner = () => {
    setShowSaveBannerModal(false);
  };

  const handleColorSelect = (colorId: string | null) => {
    if (!colorId) return;
    const selectedColorObj = colors?.find((c: IBannerColor) => c.id === colorId);
    if (selectedColorObj) {
      setSelectedColor(selectedColorObj.color);
    }
  };

  const getSelectedColorId = (): string | null => {
    const selectedColorObj = colors?.find((c: IBannerColor) => c.color === selectedColor);
    return selectedColorObj?.id || null;
  };

  const isFormValid = (): boolean => {
    const hasValidColor = !!getSelectedColorId();
    const hasValidSubjectLength = subject.length <= MAX_SUBJECT_LENGTH;
    const hasValidTextLength = bannerText.length <= MAX_TEXT_LENGTH && bannerTextEn.length <= MAX_TEXT_LENGTH;
    return !!(subject.trim() && bannerText.trim() && hasValidColor && hasValidSubjectLength && hasValidTextLength);
  };

  const createNavigationBlocker = useCallback(() => {
    return (location: any) => {
      if (checkUnsavedChanges()) {
        setShowUnsavedChangesModal(true);
        pendingNavigation.current = () => {
          unblockRef.current?.();
          history.push(location.pathname);
        };
        return false;
      }
    };
  }, [checkUnsavedChanges, history]);

  const handleBeforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      if (checkUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    },
    [checkUnsavedChanges]
  );

  useEffect(() => {
    loadAnnouncementData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const navigationBlocker = createNavigationBlocker();
    const unblock = history?.block(navigationBlocker);

    unblockRef.current = unblock;

    return () => {
      unblock?.();
    };
  }, [history, createNavigationBlocker]);

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleBeforeUnload]);

  useEffect(() => {
    if (!originalData) return;
    setHasUnsavedChanges(checkUnsavedChanges());
  }, [subject, bannerText, bannerTextEn, selectedColor, isActive, originalData, checkUnsavedChanges]);
  
  return {
    // Estados
    subject,
    bannerText,
    bannerTextEn,
    selectedColor,
    isActive,
    isEditMode,
    showUnsavedChangesModal,
    showSaveBannerModal,
    showDeactivateModal,
    isLoading,
    isSaving,
    hasUnsavedChanges,
    // Funciones de cambio
    setSubject,
    handleBannerTextChange,
    handleBannerTextEnChange,
    handleColorSelect,
    // Funciones de validación
    hasSubjectError,
    hasBannerTextError,
    hasBannerTextEnError,
    isFormValid,
    // Funciones de manejo
    handleToggleChange,
    handleSaveBanner,
    handleConfirmSaveBanner,
    handleCancelSaveBanner,
    handleConfirmDeactivate,
    handleCancelDeactivate,
    handleConfirmExit,
    handleCancelExit,
    // Funciones auxiliares
    getSelectedColorId,
    // Constantes
    MAX_TEXT_LENGTH,
    MAX_SUBJECT_LENGTH
  };
};
